import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useMsal } from '@azure/msal-react'
import { setUser, setPhoto, IUserState, updateUser } from './userSlice'
//import { getMSALCache } from '@/services/security/msalToken'
//import Config from '@/configurations'
import {
  MotifAvatar,
  MotifDropdown,
  MotifDropdownItem,
  MotifIconButton,
  MotifIcon,
} from '@ey-xd/motif-react'
import {
  useCreateUserSessionMutation,
  useEndUserSessionMutation,
  useCheckUserSessionQuery,
  useVerifyUserAccountMutation,
} from './userApi'
import { useGetMyPhotoQuery } from '@/services/graphApi'
import { skipToken } from '@reduxjs/toolkit/query'

//import { actionIcExitToApp24px } from '@ey-xd/motif-react/assets/icons'
import { MotifActionIcExitToApp24px } from '@ey-xd/motif-icon'
import { useNavigate } from 'react-router-dom'
import SessionPopup from '@/features/session/SessionPopup'

import styles from './index.module.scss'

const SESSION_KEY = "duplicateSessionToken";

// Required outside the component to ensure only one session timer is active globally
let globalSessionTimerLock = false;

const User = () => {
  const { accounts, instance } = useMsal()
  const currentUser: IUserState = useSelector(
    (state: { user: IUserState }) => state.user,
  )
  const dispatch = useDispatch()  
  const { data: photo } = useGetMyPhotoQuery(currentUser.sessionActive ? undefined : skipToken)
  const [createUserSession] = useCreateUserSessionMutation()
  const [verifyUserAccount] = useVerifyUserAccountMutation()
  const [endUserSession] = useEndUserSessionMutation()
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const navigate = useNavigate()
  const [sessionPopupVisible, setSessionPopupVisible] = useState(false)
  const sessionDuration = 60 * 60
  const warningTime = 5 * 60
  const [timeLeft, setTimeLeft] = useState<number>(sessionDuration)
  const logoutCalledRef = React.useRef(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const { data: userSessionData, refetch: refetchUserSession } = useCheckUserSessionQuery()

  useEffect(() => {
    if (logoutCalledRef.current){
      return
    }

    const currentSession = localStorage.getItem(SESSION_KEY);
    
    if (!currentSession || currentSession !== sessionToken) {
      // Set a new session token
      const newSessionToken = Date.now().toString();
      localStorage.setItem(SESSION_KEY, newSessionToken);
      setSessionToken(newSessionToken);
    }
  
    const handleStorageChange = (event: any) => {
      if (event.key === SESSION_KEY) {        
        const eventValue = event?.newValue?.toString();

        if (eventValue === null || eventValue === undefined || eventValue === sessionToken) {
          return; // No change in session token
        }

        if (eventValue !== sessionToken) {
          // Session token has changed, handle logout
          handleLogout();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  // triggers pop-up when session is a set duration (warningTime) away from expiring
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer)

          // Handle session expiry (e.g., logout user)
          handleLogout()

          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    // Show popup N minutes before session expiry
    if (timeLeft === warningTime) {
      // if the user has dismissed the popup, don't show it again
      if (
        sessionStorage.getItem('popupDismissed') === 'false' ||
        sessionStorage.getItem('popupDismissed') === null
      ) {
        setSessionPopupVisible(true)
        sessionStorage.setItem('popupDismissed', 'true')
      }
    }

    return () => clearInterval(timer) // Cleanup on unmount
  }, [timeLeft])

  /*
    const setBackendAccessToken = async (
      backendReq: SilentRequest,
      currentUser: IUserState,
    ) => {
      try {
        await instance.acquireTokenSilent(backendReq)
        const backendAccessToken = getMSALCache('accesstoken', 'default')
        if (backendAccessToken) {
          currentUser.backendAccessToken = backendAccessToken
        }
        dispatch(setUser(currentUser))
      } catch (err) {
        dispatch(setUser(currentUser))
        logger.error(err)
      }
    }
  */


  useEffect(() => {
    if (accounts.length > 0 && !currentUser.id && !logoutCalledRef.current) {
      const updatedUser = {
        ...currentUser,
        id: accounts[0].localAccountId,
        name: accounts[0].name,
        email: accounts[0].username,
        isLoading: false,
      }
      dispatch(setUser(updatedUser))
      /*
      const accessToken: null | IMSALAccessToken = getMSALCache('accesstoken')
      if (accessToken) {
        currentUser.accessToken = accessToken
      }

      const backendReq: SilentRequest = {
        scopes: [Config.APIAuthScope],
        account: accounts[0],
      }
      setBackendAccessToken(backendReq, currentUser)
      */
    }
  }, [accounts, currentUser, dispatch])
    
  useEffect(() => {
    // useGetMyPhotoQuery resolves to the raw data-URL string, not an IUserPhoto
    if (currentUser.sessionActive && photo) {
      dispatch(setPhoto({ value: photo }))
    }
  }, [currentUser.sessionActive, photo, dispatch])

  useEffect(() => {
    if (
      !currentUser.isLoading &&
      currentUser.backendAccessToken &&
      !currentUser.sessionActive &&
      !logoutCalledRef.current // Check if the user is not logging out to prevent calling session start again
    ) {
      setSessionTimers()      
    }
  }, [currentUser])
  

  const setSessionTimers = async () => {
    if (globalSessionTimerLock) return; // Prevent concurrent calls
    globalSessionTimerLock = true;

    dispatch(updateUser({ key: "isLoading", value: true }));
    try {
      const response = await createUserSession().unwrap();
      dispatch(setUser({ ...currentUser, sessionActive: true, isLoading: false, sessionId: response.sessionId }));
      // Dispatched above first so the Session-Key header (read from state.user.sessionId) is present on this call
      void verifyUserAccount({ displayName: currentUser.name ?? '' }).unwrap().catch((err) => console.log("Error in verifyUserAccount :", err));
    } catch (err) {
      console.log("Error in setSessionTimers :", err);
      /*
      const backendReq: SilentRequest = {
        scopes: [Config.APIAuthScope],
        account: accounts[0],
      };
      setBackendAccessToken(backendReq, userState);
      */
    } finally {
      setSessionPopupVisible(false);
      setTimeLeft(sessionDuration);
      globalSessionTimerLock = false; // Allow future calls
    }
  }
  
  const handleLogout = async () => {
    if (!logoutCalledRef.current) {
      logoutCalledRef.current = true

      await endUserSession()

      const emptyUser: IUserState = {
        id: '',
        name: '',
        email: '',
        photo: undefined,
        //accessToken: undefined,
        sessionActive: false,
        backendAccessToken: undefined,
        isLoading: false,
      }

      dispatch(setUser(emptyUser))
      setTimeout(() => {
        navigate('/logout')
      }, 500)
    }
  }

  // Periodically check user session
  useEffect(() => {
    if (currentUser.sessionActive === false) return;
    console.log("User - useEffect - Checking user session...", currentUser.sessionId);
    if (!currentUser.sessionId) return;

    const interval = setInterval(() => {
      refetchUserSession();
    }, 25000); // Check every 25 seconds

    return () => clearInterval(interval);
  }, [currentUser.sessionId, refetchUserSession]);

  useEffect(() => {
    if (currentUser.sessionActive === false) return;
    console.log("User - useEffect - Checking for duplicate sessions...", userSessionData, currentUser.sessionId);
    if (userSessionData && currentUser.sessionId !== userSessionData) {
      handleLogout();
    }
  }, [userSessionData]);

  if (currentUser.name && currentUser.name.length > 0) {
    return (
      <>
        <div className={styles.avatarContainer}>
          <MotifDropdown
            open={dropdownVisible}
            closeOnOutsideClick={ true }
            handleOnClose={() => setDropdownVisible(false)}
            trigger={
              <MotifIconButton
                type="button"
                onClick={() => {
                  setDropdownVisible(true)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                {currentUser.photo ? (
                  <>
                    <MotifAvatar src={currentUser.photo.value} userName={currentUser.name} />
                    <span>Hello, {currentUser.name}</span>
                  </>
                ) : (
                  <>
                    <MotifAvatar userName={currentUser.name} />
                    <span>Hello, {currentUser.name}</span>
                  </>
                )}
              </MotifIconButton>
            }
          >
            <MotifDropdownItem onClick={handleLogout}>
              <MotifIcon src={MotifActionIcExitToApp24px} />
              Logout
            </MotifDropdownItem>
          </MotifDropdown>
        </div>
        {
          //<p>Time left: {Math.floor(timeLeft / 60)}:{('0' + (timeLeft % 60)).slice(-2)}</p>
        }
        <SessionPopup
          isModalVisible={sessionPopupVisible}
          setModalVisibility={setSessionPopupVisible}
          handleLogout={handleLogout}
          warningTime={warningTime}
          handleExtend={setSessionTimers}
        />
      </>
    )
  }

  return <div className={styles.avatarContainer} />
}

export default User


